import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Grid, IconButton, Tooltip } from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack"; // Import Back Icon
// Import the ConversationsList component
import ConversationsList from "../../components/Messaging/ConversationsList";
// Import the ChatWindow component
import ChatWindow from "../../components/Messaging/ChatWindow";
// Import selector hook and user type
import { useAppSelector, useAppDispatch } from "../../store/hook";
import { RootState } from "../../store/store";
// Import the fetchNotifications action
import { fetchNotifications, Notification } from "../../store/slices/notificationsSlice";
// Import the modal component
import NewConversationModal from "../../components/Messaging/NewConversationModal";
import useResponsive from "../../hooks/useResponsive"; // Import responsive hook
import { setActiveConversation } from "../../store/slices/messagingSlice"; // Corrected import based on linter suggestion

const MessagingPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state: RootState) => state.auth.user?.id);
    const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated);
    const notifications = useAppSelector((state: RootState) => state.notifications.notifications);
    const { isTabletUp } = useResponsive(); // Use responsive hook

    // Get the active conversation ID to conditionally render ChatWindow
    const activeConversationId = useAppSelector(
        (state: RootState) => state.messaging.activeConversationId,
    );
    // Get current user's first name for the title
    const userFirstName = useAppSelector((state: RootState) => state.auth.user?.firstName);

    // Add logging for activeConversationId changes
    useEffect(() => {
        // console.log("=== [MessagingPage] activeConversationId changed ===");
        // console.log("[MessagingPage] activeConversationId:", activeConversationId);
        // console.log("[MessagingPage] isTabletUp:", isTabletUp);
        // console.log("[MessagingPage] Will show ChatWindow:", !!activeConversationId);
    }, [activeConversationId, isTabletUp]);

    // Add logging for component mount
    useEffect(() => {
        // console.log("=== [MessagingPage] Component mounted ===");
        // console.log("[MessagingPage] Initial activeConversationId:", activeConversationId);
        // console.log("[MessagingPage] userId:", userId);
        // console.log("[MessagingPage] isAuthenticated:", isAuthenticated);
    }, []);

    // State for unread message notification IDs (or relevant entityIds)
    const [unreadMessageNotificationIds, setUnreadMessageNotificationIds] = useState<string[]>([]);

    // State to control the visibility of the New Conversation modal
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch notifications on mount
    useEffect(() => {
        // console.log("[MessagingPage] Checking if should fetch notifications...");
        // console.log("[MessagingPage] isAuthenticated:", isAuthenticated, "userId:", userId);
        if (isAuthenticated && userId) {
            console.log("[MessagingPage] Fetching notifications for userId:", userId);
            dispatch(fetchNotifications(userId));
        }
    }, [dispatch, isAuthenticated, userId]);

    // Process notifications to find unread messages
    useEffect(() => {
        const unreadIds = notifications
            .filter((n: Notification) => n.type === "message" && n.status === "unread")
            .map((n: Notification) => n.entityId); // Assuming entityId links to the conversation
        setUnreadMessageNotificationIds(unreadIds);
    }, [notifications]);

    // Function to open the modal
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    // Function to close the modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // Inline styles for animations
    const mobileContainerStyle: React.CSSProperties = !isTabletUp
        ? {
              position: "relative",
              overflowX: "hidden",
              width: "100%",
              height: `calc(100vh - 135px)`, // Full viewport height minus navbar and some padding
          }
        : {};

    // Styles for the animating panes
    const listPaneStyle: React.CSSProperties = !isTabletUp
        ? {
              transform: activeConversationId ? "translateX(-100%)" : "translateX(0%)",
              transition: "transform 0.3s ease-in-out",
              width: "100vw",
              height: "100%",
              zIndex: 1, // Explicit zIndex
              willChange: "transform", // Animation hint
          }
        : {};

    const chatPaneStyle: React.CSSProperties = !isTabletUp
        ? {
              transform: activeConversationId ? "translateX(0%)" : "translateX(100%)",
              transition: "transform 0.3s ease-in-out",
              width: "100vw",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 2, // Higher zIndex
              // backgroundColor: 'var(--card-background)', // Temporarily commented for testing
              willChange: "transform", // Animation hint
          }
        : {};

    // TODO: Fetch settings page style/structure later for closer match

    return (
        <Box sx={{ flexGrow: 1, padding: isTabletUp ? 3 : 0, paddingTop: isTabletUp ? 3 : "0px" }}>
            {isTabletUp ? (
                // DESKTOP LAYOUT
                <Grid container spacing={2}>
                    <Grid item md={4}>
                        <Paper
                            elevation={2}
                            sx={{
                                height: "calc(100vh - 150px)",
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: "12px",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 16px",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    flexShrink: 0,
                                }}
                            >
                                <Typography variant="h6" sx={{ padding: "8px 0px", flexShrink: 0 }}>
                                    Conversations
                                </Typography>
                                {/* Tooltip and IconButton for new message can go here if re-enabled */}
                            </Box>
                            <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                                <ConversationsList
                                    unreadConversationIds={unreadMessageNotificationIds}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item md={8}>
                        <Paper
                            elevation={0}
                            sx={{
                                height: "calc(100vh - 150px)",
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: "12px",
                                overflow: "hidden",
                                position: "relative",
                                backgroundColor: "transparent",
                            }}
                        >
                            {activeConversationId ? (
                                <ChatWindow />
                            ) : (
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        padding: 2,
                                        textAlign: "center",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "text.secondary",
                                        backgroundColor: "background.default",
                                    }}
                                >
                                    <Typography variant="h6">
                                        Select a conversation to start chatting.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            ) : (
                // MOBILE LAYOUT (ANIMATED PANES)
                <Box sx={mobileContainerStyle}>
                    {/* Conversations List Pane */}
                    <Box style={listPaneStyle}>
                        <Paper
                            elevation={0}
                            sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: "0",
                                overflow: "hidden",
                                boxShadow: "none",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 16px",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    flexShrink: 0,
                                }}
                            >
                                <Typography variant="h6" sx={{ padding: "8px 0px", flexShrink: 0 }}>
                                    Conversations
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                                <ConversationsList
                                    unreadConversationIds={unreadMessageNotificationIds}
                                />
                            </Box>
                        </Paper>
                    </Box>

                    {/* Chat Window Pane - only mount ChatWindow if a conversation is active */}
                    {activeConversationId && (
                        <Box style={chatPaneStyle}>
                            <Paper
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: "0",
                                    overflow: "hidden",
                                    boxShadow: "none",
                                    position: "relative",
                                    backgroundColor: "transparent",
                                }}
                            >
                                {/* <IconButton onClick={handleMobileBack} sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10, backgroundColor: 'rgba(255,255,255,0.7)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } }} aria-label="back to conversations">
                                    <ArrowBackIcon />
                                </IconButton> */}
                                <ChatWindow />
                            </Paper>
                        </Box>
                    )}
                </Box>
            )}
            <NewConversationModal open={isModalOpen} onClose={handleCloseModal} />
        </Box>
    );
};

export default MessagingPage;
